import React, { useEffect, useMemo } from "react";
import { faEnvelope, faMobile, faPhone, faPhoneVolume } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { useQueryState } from "nuqs";
import { Column as ReactTableColumn, useColumnOrder, useSortBy, useTable } from "react-table";
import { AutoSizer, Column, InfiniteLoader, Table } from "react-virtualized";
import { Checkbox, Icon, Loader, Popup, Segment } from "semantic-ui-react";
import { parsedPhoneNumberAsString } from "../../../../../components/global_components/form_components/ControlledPhoneNumber";
import { pluralize } from "../../../../../utils/pluralize";
import { nuqsSortParser } from "../../../util/nuqsSortParser";

import "./NotifySubscribersInfiniteTable.scss";
import ContactNameWithAvatarPopup from "../../../../contacts/components/ContactNameWithAvatarPopup";
import { buildAppIdentifierString } from "./utils/buildAppIdentifierString";
import { useInfiniteNotifyContacts } from "../../../hooks/useInfiniteNotifyContacts";
import { OverflowTooltip } from "../../../../global/components/tooltips/OverflowTooltip";
import { faUser, faBellSlash } from "@fortawesome/pro-solid-svg-icons";
import { formatFriendlyDate } from "../../../../../utils/formatFriendlyDate";
import { SubscriberDetailFlyout } from "./flyouts/SubscriberDetailFlyout";
import { useFlyout } from "../../../../../components/global_components/Flyout";
import { NotifyContactDetailFlyout } from "./flyouts/NotifyContactDetailFlyout";

export type NotifySubscribersInfiniteTableType = "Phone" | "Email" | "App";
export type NotifySubscribersInfiniteTableView = "Enrolled" | "Invited" | "OptedOut";

export function NotifyContactsInfiniteTable({
  filters,
}: {
  filters: {
    status: NotifySubscribersInfiniteTableView;
    query: string;
    subscriptionGroups: number[];
    types: NotifySubscribersInfiniteTableType[];
  };
}) {
  const [sortBy, setSortBy] = useQueryState("sort", nuqsSortParser.withDefault([{ id: "createdAt", desc: true }]));

  const toggleSortBy = (columnId: string, clearPrevious = false) => {
    setSortBy((prev) => {
      const existingSort = prev.find((sort) => sort.id === columnId);

      if (clearPrevious) {
        if (existingSort) return [{ id: columnId, desc: !existingSort.desc }];
        return [{ id: columnId, desc: true }];
      }

      if (existingSort) return prev.map((sort) => (sort.id === columnId ? { ...sort, desc: !sort.desc } : sort));
      return [...prev, { id: columnId, desc: true }];
    });
  };

  const {
    notifyContacts,
    notifyContactsTotal,
    fetchMoreNotifyContacts,
    isNotifyContactsFetching,
    isNotifyContactsLoading,
  } = useInfiniteNotifyContacts({
    take: 100,
    query: filters.query,
    status: filters.status,
    subscriptionGroupIds: filters.subscriptionGroups,
    types: filters.types,
    sort: sortBy.map(({ id, desc }) => ({ column: id, direction: desc ? "desc" : "asc" })),
  });

  const notifyContactColumns = useMemo<ReactTableColumn<(typeof notifyContacts)[0]>[]>(
    () => [
      {
        accessor: "id",
        Header: ({}) => <Checkbox indeterminate={false} checked={false} />,
        Cell: ({}) => <Checkbox checked={false} />,
        disableSortBy: true,
        width: 40,
      },
      {
        accessor: "person",
        id: "person",
        Header: "Contact",
        Cell: ({ value, row }) => {
          if (value) {
            return (
              <ContactNameWithAvatarPopup
                personId={value.personId}
                firstName={value.firstName ?? ""}
                lastName={value.lastName ?? ""}
              />
            );
          }
          const potentialMatchNames = row.original.contactMethods
            .flatMap((cm) => cm.potentialPersonMatches)
            .map((m) => [m.firstName, m.lastName].join(" "));

          if (potentialMatchNames.length > 0) {
            return (
              <OverflowTooltip className='tw-whitespace-nowrap'>
                {potentialMatchNames.length > 1
                  ? `${potentialMatchNames[0]} + ${potentialMatchNames.length - 1}`
                  : potentialMatchNames[0]}
              </OverflowTooltip>
            );
          } else {
            return null;
          }
        },
        minWidth: 200,
      },
      {
        Header: "",
        id: "view",
        Cell: ({ row }) => {
          const contact = row.original.person
            ? `${row.original.person.firstName} ${row.original.person.lastName}`
            : "Anonymous Contact";
          const contactType = row.original.type || "Unknown Type";
          const contactMethods =
            row.original.contactMethods.map((method) => method.identifier).join(", ") || "No Contact Methods";

          return (
            <button
              aria-label={`View details for ${contact}, Type: ${contactType}, Methods: ${contactMethods}`}
              className='tw-m-0 tw-cursor-pointer tw-border-none tw-bg-transparent tw-p-0 tw-text-primary-hover tw-opacity-0 tw-transition-opacity focus:tw-opacity-100'
              tabIndex={0}
            >
              View
            </button>
          );
        },
        width: 60,
        disableSortBy: true,
      },
      {
        accessor: "type",
        Header: "Type",
        id: "notifyContactType",
        Cell: ({ value }) => {
          const iconLookup: Record<string, Function> = {
            Email: () => <FontAwesomeIcon className='tw-text-purple' icon={faEnvelope} />,
            Phone: () => <FontAwesomeIcon className='tw-text-orange' icon={faPhone} />,
            App: () => <FontAwesomeIcon className='tw-text-teal' icon={faMobile} />,
            Contact: () => <FontAwesomeIcon className='tw-text-primary-icon' icon={faUser} />,
          };
          return (
            <div className='tw-space-x-3 tw-whitespace-nowrap'>
              {iconLookup[value]()}
              <span>{value}</span>
            </div>
          );
        },
        width: 100,
      },
      {
        accessor: "contactMethods",
        Header: "Contact Methods",
        id: "notifyContactContactMethodIdentifiers",
        Cell: ({ value }) => {
          return (
            <OverflowTooltip className='tw-line-clamp-2 tw-w-full tw-overflow-hidden tw-text-ellipsis tw-break-words tw-leading-snug'>
              <span>
                {value.map((method, index) => {
                  let content;

                  if (method.type === "Phone") {
                    content = (
                      <>
                        {parsedPhoneNumberAsString(method.identifier)}
                        {method.isPhoneNumberSMSCapable === false && (
                          <Popup
                            content='This number cannot receive SMS messages'
                            trigger={<FontAwesomeIcon icon={faPhoneVolume} className='tw-ml-1' />}
                            inverted
                            position='top left'
                          />
                        )}
                      </>
                    );
                  } else if (method.type === "App") {
                    const deviceName = buildAppIdentifierString(
                      method.deviceMetadata?.platformDetails,
                      method.deviceMetadata?.deviceType,
                    );
                    content = deviceName;
                  } else {
                    content = method.identifier;
                  }

                  return (
                    <>
                      {content}
                      {index < value.length - 1 && ", "}
                    </>
                  );
                })}
              </span>
            </OverflowTooltip>
          );
        },
        width: 250,
      },
      {
        accessor: "oldestJoinedAt",
        Header: "Joined",
        id: "createdAt",
        Cell: ({ value }) => {
          return <div>{formatFriendlyDate(new Date(value))}</div>;
        },
        minWidth: 120,
      },
      {
        accessor: "mostRecentCommunicationSendAttemptedAt",
        Header: "Last Sent",
        id: "mostRecentCommunicationSendAttemptedAt",
        Cell: ({ value }) => {
          // TODO: Upgrade client to make sure mostRecentJoinedAt is always defined
          if (value === null) {
            return <div></div>;
          }
          return <div>{formatFriendlyDate(new Date(value))}</div>;
        },
        minWidth: 120,
      },
      {
        accessor: "contactMethods",
        Header: "Subscription Groups",
        id: "notifyContactContactMethodSubscriptionGroups",
        Cell: ({ value }) => {
          const subscriptionGroupNames = (() => {
            const seenGroupIds = new Set(); // Track unique group IDs

            return value.flatMap((contactMethod) => {
              // Filter out autoSubscribe groups
              return contactMethod.subscriptionGroups
                .filter((group) => !group.autoSubscribe)
                .map((group) => {
                  if (seenGroupIds.has(group.id)) {
                    return null;
                  }
                  seenGroupIds.add(group.id);

                  // Find the subscription preference for this group
                  const preference = contactMethod.subscriptionPreferences.find(
                    (pref) => pref.subscriptionGroupId === group.id,
                  );

                  // Determine the icon based on the preference
                  let icon = null;
                  if (preference?.type === "Unsubscribe") {
                    icon = <FontAwesomeIcon icon={faBellSlash} className='tw-ml-1 tw-text-primary-hint' />;
                  }

                  return (
                    <span key={group.id} className='tw-flex tw-items-center'>
                      {group.name}
                      {icon}
                    </span>
                  );
                })
                .filter(Boolean); // Remove null values
            });
          })();

          const content = (
            <span className='tw-inline'>
              <span className='tw-italic'>All auto subscribe groups</span>
              {subscriptionGroupNames.length > 0 && (
                <span>
                  ·
                  {subscriptionGroupNames.map((item, index) => (
                    <span key={index} className='tw-inline-flex tw-items-center'>
                      {item}
                      {index < subscriptionGroupNames.length - 1 && " · "}
                    </span>
                  ))}
                </span>
              )}
            </span>
          );

          return (
            <Popup
              content={
                <div>
                  <div className='tw-inline-block'>{content}</div>
                  <div className='tw-text-primary-hint'>
                    <FontAwesomeIcon icon={faBellSlash} />
                    Notifications are muted for this private/unlisted group, but they are still a member
                  </div>
                </div>
              }
              mouseEnterDelay={500}
              mouseLeaveDelay={200}
              on='hover'
              className='tw-min-w-[350px]'
              trigger={
                <span className='tw-inline-block tw-max-w-full tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-align-middle'>
                  {content}
                </span>
              }
            />
          );
        },
        flexGrow: 1,
        disableSortBy: true,
      },
    ],
    [filters.status],
  );

  const { rows, prepareRow, headers, setHiddenColumns, setColumnOrder } = useTable(
    {
      columns: notifyContactColumns,
      data: notifyContacts,
      useControlledState: (state) => {
        return useMemo(
          () => ({
            ...state,
            sortBy,
          }),
          [state, sortBy],
        );
      },
      manualSortBy: true,
    },
    useSortBy,
    useColumnOrder,
  );

  const totalWidth = headers.reduce((acc, column) => {
    if (!column.isVisible) return acc;
    if (column.width) return Number(column.width) + acc;
    return column.minWidth ?? acc;
  }, 0);

  useEffect(() => {
    if (filters.status === "Enrolled") {
      setColumnOrder([
        "id",
        "person",
        "notifyContactType",
        "notifyContactContactMethodIdentifiers",
        "view",
        "createdAt",
        "mostRecentCommunicationSendAttemptedAt",
        "notifyContactContactMethodSubscriptionGroups",
      ]);
    }
  }, [setHiddenColumns, setColumnOrder, filters.status]);

  const getFoundTextUnit = () => {
    if (filters.status === "Enrolled") {
      return pluralize(notifyContactsTotal, "subscription", "subscriptions");
    }
    return "";
  };

  const {
    openWithId: openSubscriberDetailFlyoutWithId,
    close: closeSubscriberDetailFlyout,
    id: subscriberDetailFlyoutId,
  } = useFlyout();

  const {
    openWithId: openNotifyContactDetailFlyoutWithId,
    close: closeNotifyContactDetailsFlyout,
    id: notifyContactFlyoutId,
  } = useFlyout();

  return (
    <>
      <SubscriberDetailFlyout
        view={filters.status}
        subscriberId={subscriberDetailFlyoutId}
        onClose={closeSubscriberDetailFlyout}
      />
      <NotifyContactDetailFlyout notifyContactId={notifyContactFlyoutId} onClose={closeNotifyContactDetailsFlyout} />
      <Segment attached='top' className='subText tw-flex tw-flex-col tw-gap-3 tw-px-[11px] tw-py-2 md:tw-flex-row'>
        {isNotifyContactsLoading ? (
          <span>Loading...</span>
        ) : (
          <span className='tw-flex tw-items-center tw-gap-2'>
            <span>
              Found {notifyContactsTotal} {getFoundTextUnit()}
            </span>

            <Loader inline active={isNotifyContactsFetching} size='tiny' />
          </span>
        )}
      </Segment>
      <Segment
        attached='bottom'
        className='notify-subscribers-infinite-table tw-h-full tw-overflow-x-auto tw-overflow-y-hidden tw-p-0'
      >
        <InfiniteLoader
          isRowLoaded={({ index }) => !!rows[index]}
          loadMoreRows={async () => fetchMoreNotifyContacts()}
          rowCount={notifyContactsTotal}
        >
          {({ onRowsRendered, registerChild }) => (
            <AutoSizer>
              {({ width, height }) => (
                <Table
                  headerHeight={45}
                  rowCount={rows.length}
                  rowHeight={45}
                  ref={registerChild}
                  width={Math.max(width - 2, totalWidth)} // Magic 2 to account for border
                  height={height}
                  onRowsRendered={onRowsRendered}
                  rowGetter={({ index }) => {
                    const row = rows[index];
                    prepareRow(row);
                    return row;
                  }}
                  onRowClick={({ rowData }) => {
                    if (rowData.original.type === "Contact") {
                      openNotifyContactDetailFlyoutWithId(rowData.original.id);
                      return;
                    }
                    openSubscriberDetailFlyoutWithId(rowData.original.contactMethods[0].id);
                  }}
                  style={{ minWidth: `${Math.max(width - 2, totalWidth)}px` }} // Magic 2 to account for border
                >
                  {headers
                    .filter((column) => column.isVisible)
                    .map((column) => (
                      <Column
                        key={column.id}
                        dataKey={column.id}
                        maxWidth={column.maxWidth}
                        minWidth={column.minWidth}
                        flexGrow={(column as any).flexGrow}
                        flexShrink={(column as any).flexShrink}
                        width={typeof column.width === "number" ? column.width : 150}
                        headerClassName={classNames({
                          "tw-cursor-pointer": column.canSort,
                        })}
                        headerRenderer={() => (
                          <div
                            role='button'
                            tabIndex={-1}
                            onClick={() => {
                              if (column.canSort) {
                                toggleSortBy(column.id, true);
                              }
                            }}
                          >
                            {column.render("Header")}
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <Icon name='caret down' />
                              ) : (
                                <Icon name='caret up' />
                              )
                            ) : (
                              ""
                            )}
                          </div>
                        )}
                        cellRenderer={({ rowIndex, columnIndex }) => {
                          return rows[rowIndex].cells[columnIndex].render("Cell");
                        }}
                      />
                    ))}
                </Table>
              )}
            </AutoSizer>
          )}
        </InfiniteLoader>
      </Segment>
    </>
  );
}
